import { useState, useEffect } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import AdminService from "../services/adminService";
import LoadingForButton from "./LoadingForButton";
import { useDispatch } from "react-redux";
import { refreshDataGuest } from "../store/guestSlice";
import { useTranslation } from "react-i18next";

const EditTableModal = ({ table, onClose, setShowNotification, setNotificationMessage, setErrorResponse }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: table.name,
        seats: table.seats
    });

    const originalData = {
        name: table.name,
        seats: table.seats
    };

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [invalidCredentials, setInvalidCredentials] = useState("");

    const dispatch = useDispatch();

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    useEffect(() => {
        setHasChanges(JSON.stringify(formData) !== JSON.stringify(originalData));
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: false }));
        setInvalidCredentials("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        const newErrors = {};
        const nameRegex = /^[a-zA-Z0-9čćđšžČĆĐŠŽ\s-]+$/;

        if (!formData.name.trim()) {
            newErrors.name = t("tableNameRequired");
        } else if (!nameRegex.test(formData.name.trim())) {
            newErrors.name = t("tableNameInvalid");
        } else if (formData.name.trim().length > 50) {
            newErrors.name = t("maxLengthError", { count: 50 });
        }
        const seatsValue = parseInt(formData.seats, 10);
        if (!formData.seats) {
            newErrors.seats = t("tableSeatsRequired");
        } else if (isNaN(seatsValue) || seatsValue <= 0 || seatsValue > 100) {
            newErrors.seats = t("tableSeatsInvalid");
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setLoading(false);
            return;
        }

        AdminService.updateTable(table._id, {
            name: formData.name,
            seats: formData.seats,
            isLocked: table.isLocked
        })
            .then(() => {
                setLoading(false);
                dispatch(refreshDataGuest());
                setNotificationMessage(t("tableUpdatedSuccess"));
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
                onClose();
            })
            .catch((err) => {
                if (err.response?.data?.message === "Table with the same name and wedding already exists") {
                    setInvalidCredentials(t("tableExists"));
                } else {
                    setInvalidCredentials(t("genericError"));
                }
                setLoading(false);
            });
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 p-4">
            <div className="relative p-4 border rounded-lg shadow-lg bg-white w-full max-w-2xl mx-auto lg:h-auto max-h-screen overflow-y-auto">
                <button className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl" onClick={onClose}>
                    <FaTimes />
                </button>
                <h2 className="text-2xl font-semibold text-center mb-5 flex items-center justify-center">
                    {t("editTable")}
                </h2>
                <p className="text-gray-600 mb-7">{t("editTableDesc")}</p>

                <form onSubmit={handleSubmit} className="flex flex-col space-y-7 mb-10">
                    <div className="flex flex-col">
                        <label className="font-semibold flex items-center mb-1">{t("tableName")}</label>
                        <input
                            type="text"
                            name="name"
                            maxLength={50}
                            value={formData.name}
                            onChange={handleChange}
                            className={`border px-3 py-2 rounded-md ${errors.name ? "border-red-500" : "border-gray-400"}`}
                        />
                        {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                    </div>

                    <div className="flex flex-col">
                        <label className="font-semibold flex items-center mb-1">{t("numberOfSeats")}</label>
                        <input
                            type="number"
                            name="seats"
                            min={1}
                            max={20}
                            value={formData.seats}
                            onChange={handleChange}
                            className={`border px-3 py-2 rounded-md ${errors.seats ? "border-red-500" : "border-gray-400"}`}
                        />
                        {errors.seats && <p className="text-red-500 text-sm">{errors.seats}</p>}
                    </div>

                    {invalidCredentials && (
                        <p className="bg-red-100 rounded-lg p-2 text-red-700 text-center my-4">
                            {invalidCredentials}
                        </p>
                    )}

                    <button
                        type="submit"
                        className={`px-4 py-2 w-full h-12 flex justify-center items-center ml-auto rounded-xl text-white ${hasChanges ? "bg-blue-600" : "bg-gray-300 cursor-not-allowed"}`}
                        disabled={!hasChanges || loading}
                    >
                        {loading ? <LoadingForButton /> : t("saveChanges")}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default EditTableModal;
